import { gatewayInstance } from "../server/gateway";
import { MeetingPlatform, MeetingQueryType } from "@/types/common";
import { NPCRoom } from "@/components/MeetingCloudLink/Invites/NPC";
import { getClientType } from "@/utils";
import Employee from "@jd/jdee.im.sdk/lib/es/model/Employee";
import { getUnitName } from "@/baseComponents/Meeting/utils";

export interface CreateMeetingResult {
  controlPassword: any;
  meetingId: string;
  uuid: string;
  topic: string;
  startTime: string;
  duration: number;
  password: string;
  joinUrl: string;
}
export async function createMeeting(opts: {
  startTime: string;
  duration: number;
  topic: string;
  password: string;
  participantIds: {
    userId: string;
    teamId: string;
    appId: string;
    realName: string;
    headImg: string;
  }[];
  roomSettingId?: number; // 会议室配置id
  platform?: MeetingPlatform;
  isCycle?: boolean;
  pin?: string;
  proxyPin?: string;
  room?: NPCRoom;
  terminalId?: any;
}): Promise<[CreateMeetingResult | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.subConference",
      method: "POST",
      body: opts
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export async function cancelMeeting(opts: {
  meetingId: string;
  meetingUuid: string;
  meetingType: number;
  cancelCycle: boolean; // 是否取消循环会议，取消=true，其他场景false
  beginTime: number;
  endTime: number;
  platform?: MeetingPlatform;
}): Promise<any> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.cancelConference",
      method: "POST",
      body: {
        conferenceCode: opts.meetingId,
        conferenceUuid: opts.meetingUuid,
        conferenceKind: opts.meetingType === 1 ? "pcpm" : "cuvc",
        cancelCycle: opts.cancelCycle,
        beginTime: opts.beginTime,
        endTime: opts.endTime,
        platform: opts.platform
      }
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, error.message.toString()];
  }
}

export async function meetingPreLogin(appId: string): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.preLogin",
      method: "POST",
      body: { appId }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message?.toString() || error.errorMsg || error || "服务器错误"];
  }
}

/**
 * 查询会议
 * @param startIndex
 * @param pageSize
 * @param queryType
 * @returns
 */
// eslint-disable-next-line max-params
export async function queryMeeting({
  page = 1,
  pageSize = 10,
  conferenceTypeList = [0],
  queryType = MeetingQueryType.ONGOING_AND_UPCOMING,
  platform = MeetingPlatform.CLOUD_UNION
}: {
  page: number;
  pageSize: number;
  conferenceTypeList: number[];
  queryType: MeetingQueryType;
  platform: MeetingPlatform;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.queryConference",
      method: "POST",
      body: {
        queryType,
        conferenceTypeList, // 会议类型 0：视频会议 1：VoIP
        platform,
        pager: {
          startIndex: page - 1,
          pageSize
        }
      }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 主持人重命名
 * @param body
 */
export async function meetingRename(body: {
  appId: string;
  teamId: string;
  userId: string;
  fullName: string;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.rename",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 获取主持人职位
 * @param body
 */
export async function getUserSimple(body: {
  appId: string;
  teamId: string;
  userId: string;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.getUserSimple",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 关联会议日程
 * @param body
 */
export async function associateSchedule(body: {
  conferenceCode: string; // 会议号
  scheduleId: string; // 日程ID
  calendarId: string; // 日历ID
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.associateSchedule",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 修改会议
 * @param body
 */
export async function modifyMeeting(opts: {
  meetingId: string;
  meetingUuid: string;
  appId: string;
  teamId: string;
  userId: string;
  duration: number; // 持续时间
  topic: string; // 会议密码
  password: string;
  startTime: number; // 开始时间
  meetingType: number; // 会议类型
  option_jbh: boolean; // 主持人是否入会
  platform?: MeetingPlatform;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.modifyConference",
      method: "POST",
      body: {
        conferenceCode: opts.meetingId,
        conferenceUuid: opts.meetingUuid,
        appId: opts.appId,
        teamId: opts.teamId,
        userId: opts.userId,
        duration: opts.duration,
        topic: opts.topic,
        password: opts.password,
        startTime: opts.startTime,
        meetingType: opts.meetingType,
        option_jbh: opts.option_jbh,
        platform: opts.platform
      }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 华为批量取号接口
 * @param body
 */
export async function cloudlinkRegister(body: {
  userIds: string[]; // 用户id列表
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.register",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, error.message.toString()];
  }
}

/**
 * 发送卡片消息接口
 * @param body
 */
export async function notice(body: { userId: string }): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.notice",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 华为消息卡片获取账号信息接口
 * @param body
 */
export async function takeAccount(body: { userId: string }): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.takeAccount",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 华为获取终端列表
 * @param body
 */
export async function takeTerminals(
  pageNo = 1,
  pageSize = 20,
  name?: string
): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.takeTerminals",
      method: "POST",
      body: {
        pageNo,
        pageSize,
        name
      }
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, error.message.toString()];
  }
}

/**
 * 华为添加设备或人大会议
 * @param body
 */
export async function addParticipants(body: {
  conferenceUuid: string;
  terminalId?: string[];
  room?: NPCRoom | null;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.addParticipants",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    console.error(error);
    return [null, error.message.toString()];
  }
}

/**
 * 发起会议/加入会议 更新信息接口
 * @param body
 */
export async function updateRecentLoginTime(body: {
  userIds: string[]; // 用户id列表
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.updateRecentLoginTime",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 京东会议获取token
 * @param body
 */
export async function getJRTCToken({
  currentEmployee,
  temporary = false
}: {
  currentEmployee: Employee | Partial<Employee>;
  temporary?: boolean;
}): Promise<[any, string | null]> {
  try {
    const ext = {
      imExtKeys: {
        unitName: currentEmployee.unitName,
        unitId: currentEmployee.unitDeptId,
        deptName: currentEmployee.deptName,
        deptId: currentEmployee.deptId,
        teamId: currentEmployee.teamId,
        appId: currentEmployee.app,
        titleName: currentEmployee.titleName,
        titleId: ""
      }
    };
    const data: any = await gatewayInstance.request({
      api: "conference.getJRTCToken",
      method: "POST",
      body: {
        deviceType: getClientType(),
        nickname: currentEmployee.name,
        unit: getUnitName(currentEmployee),
        avatar: currentEmployee.avatar,
        temporary,
        ext: JSON.stringify(ext)
      }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 发起会议/加入会议 更新信息接口
 * @param body
 */
export async function conferenceCall(body: {
  userIds: string[]; // 用户id列表
  meetingId: string; // 会议号
  password: string; // 会议密码
  sdkType: string;
  topic: string; // 会议主题
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.call",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 通过会议号获取会议详情
 * @param body
 */
export async function getConferenceByCode(body: {
  meetingId: string;
  platform: MeetingPlatform;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.getConferenceByCode",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 获取会议邀请短链接
 * @param body
 */
export async function getConferenceDeeplink(body: {
  meetingId: string;
  meetingCode: string;
  platform: MeetingPlatform;
}): Promise<[any, any]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.deeplink",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 获取会议列表
 */

export async function getConferencePlatforms(): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.v2.platforms",
      method: "POST"
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// 发送消息卡片
export async function sendConferenceCardMsg(body: {
  noticeId: string;
  title: string;
  receivers: {
    pin: string;
    teamId: string;
    app: string;
  }[];
  meetingInfoReq: {
    meetingNumber: string;
    initiator: string;
    createTime?: string;
    inviteTime?: string;
    type?: number; // 0会议 1直播
  };
  infox: Record<string, any>;
}): Promise<[any, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sendConferenceCardMsg",
      method: "POST",
      body: {
        ...body,
        applicationKey: body.noticeId.replace("eopen-push_", "")
      }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export enum LiveStatus {
  LIVE_PROCESSING = "0", // ("0","直播进行中"),
  LIVE_FULL = "1", // ("1","直播间人数已满"),
  LIVE_LOCKED = "2", // ("2","直播间被锁定，禁止加入"),
  LIVE_NOT_STARTED = "3", // ("3","直播未开始"),
  LIVE_ENDED = "4" // ("4","直播已结束");
}

export function getLiveStatusText(status: LiveStatus) {
  let text = "";
  switch (status) {
    case LiveStatus.LIVE_ENDED:
      text = "直播已结束";
      break;
    case LiveStatus.LIVE_LOCKED:
      text = "直播间被锁定，禁止加入";
      break;
    case LiveStatus.LIVE_FULL:
      text = "直播间人数已满";
      break;
    case LiveStatus.LIVE_NOT_STARTED:
      text = "直播未开始";
      break;
    default:
      break;
  }
  return text;
}

// 查询直播信息
export async function queryLiveInfo(body: {
  liveCode: string;
  livePassword: string;
}): Promise<
  [
    {
      liveUrl: string;
      conferenceTitle: string;
      meetingId: string;
      numberLimit: number;
      liveStatus: LiveStatus;
      meetingCode: string;
    } | null,
    string | null
  ]
> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.live.queryLiveInfo",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 通过会议id查询直播信息
 * @param conferenceCode string
 */
export async function getLiveByConference(
  conferenceCode: string
): Promise<
  [
    {
      liveCode: string;
      livePassword: string;
      liveUrl: string;
      conferenceTitle: string;
      currentNumber: number;
      liveStatus: string;
      numberLimit: number;
    } | null,
    string | null
  ]
> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.live.getLiveByConference",
      method: "POST",
      body: { conferenceCode }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 通过会议id查询直播信息
 * @param conferenceCode string
 */
export async function exitLive(
  meetingCode: string,
  liveCode: string
): Promise<
  [
    {
      numberLimit: number;
      currentNumber: number;
      meetingCode: string;
      liveCode: string;
    } | null,
    string | null
  ]
> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.live.exitLive",
      method: "POST",
      body: { liveCode, meetingCode }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export interface LiveModel {
  meetingCode?: string; // 会议号
  startTime: number; // 是,会议开始时间
  duration: number; // 是,会议持续时间/分钟
  topic: string; // 是,会议主题
  password: string; // 否,会议密码
  participantIds: {
    userId: string;
    teamId: string;
    appId: string;
    realName: string;
    headImg: string;
  }[]; // 是,参会人员Id列表
  pin: string; // 是,会议创建人，主持人（真正主持人，不是代建人）
  proxyPin: string; // 否,会议代建人
  platform: string; // 是,会议平台；默认：JDTC即可
  numberLimit: number; // 是,直播间人数限制
}

/**
 * 预约直播接口（同时会预约会议）
 * @param body LiveModel
 */
export async function subLive(
  body: LiveModel
): Promise<
  [
    {
      meetingCode: string; // 会议号
      uuid: string; // 会议uuid
      topic: string; // 会议主题
      startTime: string; // 会议预约的开始时间：yyyy-MM-dd HH:mm:ss
      duration: number; // 会议持续时长，分钟单位
      meetingPassword: string; // 会议密码
      liveCode: string; // 直播id
      livePassword: string; // 直播密码
      liveUrl: string; // 直播url
    } | null,
    string | null
  ]
> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.live.subLive",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 取消预约直播（同时会取消预约的会议，会议不可进入）
 * @param body
 */
export async function cancelLive(body: any): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.live.cancelLive",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

/**
 * 修改预约直播
 * @param body
 */
export async function modifyLive(body: LiveModel): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.live.modifyLive",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 获取视频会议token接口-新增（拿着token请求心跳接口）
 */
export async function getLiveToken(): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.getToken",
      method: "POST"
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 导出智能会议纪要
 */
export async function exportMeetingMinutes({
  meetingId,
  type
}: {
  meetingId: string;
  type: number;
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.exportMeetingMinutes",
      method: "POST",
      body: { meetingId, type }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
export enum SignForm {
  PERSONAL = "0", // 按个人
  DEPT = "1", // 按部门
  UNIT = "2" // "按单位"
}

export enum SignStatus {
  NOT_EXIST = "-1", // 不存在
  ONGOING = "0", // 进行中
  MANUAL_END = "1", // 提前截止
  TIME_END = "2" // 签到截止"
}
export enum UserSignStatus {
  SIGNED = "1",
  NOT_SIGN = "0"
}
export enum SyncEventType {
  START = "checkIn.initiated",
  SYNC = "checkIn.information-synchronization",
  END = "checkIn.end"
}

export function getSignTypeText(type: SignForm) {
  let text = "未知";
  switch (type) {
    case SignForm.PERSONAL:
      text = "按个人";
      break;
    case SignForm.DEPT:
      text = "按部门";
      break;
    case SignForm.UNIT:
      text = "按单位";
      break;

    default:
      break;
  }
  return text;
}
export function getSignStatusText(type: SignStatus) {
  let text = "未知";
  switch (type) {
    case SignStatus.ONGOING:
      text = "进行中";
      break;
    case SignStatus.MANUAL_END:
      text = "已结束";
      break;
    case SignStatus.TIME_END:
      text = "已结束";
      break;
    default:
      break;
  }
  return text;
}
/**
 * 发起签到
 */
export async function initiateSign(body: {
  bizId: string; // 业务ID-会议id
  duration: number; // 签到时长；秒
  signForm: SignForm;
  signDesc?: string; // 签到说明
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.initiate",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 发起签到
 */
export async function extendTime(body: {
  bizId: string; // 业务ID-会议id
  signId: string; // 签到id
  extendTime: number; // 延长时间：秒
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.extendTime",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 终止签到
 */
export async function terminateSign(body: {
  bizId: string; // 业务ID-会议id
  signId: string; // 签到id
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.terminate",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

export interface SignInfo {
  duration: number;
  signStatus: SignStatus;
  bizId: string;
  signForm: SignForm;
  userSignStatus: string;
  signDesc: string;
  startTime: number;
  endTime: number;
  currentNumber: number;
  signId: string;
  remark: string;
}
/**
 * 查询签到信息
 */
export async function querySignInfo(body: {
  bizId: string; // 业务ID-会议id
  signId?: string; // 签到id
}): Promise<[SignInfo | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.queryInfo",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 查询签到列表
 */
export async function querySignList(body: {
  bizId: string; // 业务ID-会议id
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.queryList",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 导出智能会议摘要
 */
export async function exportMeetingSummary({
  meetingId,
  type
}: {
  meetingId: string;
  type: number;
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.exportMeetingSummary",
      method: "POST",
      body: { meetingId, type }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/**
 * 查询签到列表
 */
export async function querySignRecord(body: {
  bizId: string; // 业务ID-会议id
  signId: string; // 签到id
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.queryRecord",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// 倒计时同步
export async function syncTime(body: {
  bizId: string; // 业务ID-会议id
  signId: string; // 签到id
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.syncTime",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}

// 用户签到
export async function sign(body: {
  bizId: string; // 业务ID-会议id
  signId: string; // 签到id
  remark: string; // 备注
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// 查询用户当前签到是否已经签到
export async function checkUserSign(body: {
  bizId: string; // 业务ID-会议id
  signId?: string; // 签到id
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.sign.checkUserSign",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
// 试用申请
export async function checkFreeUse(body: {
  applicationId: string;
}): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.checkFreeUse",
      method: "POST",
      body
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/** 查询会议室配置 */
export async function queryRoomSetting(): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.queryRoomSetting",
      method: "POST"
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
/** 加入会议前置-根据会议号返回会议类型，用户能否加入 */
export async function preJoin(meetingCode: string): Promise<[any | null, string | null]> {
  try {
    const data: any = await gatewayInstance.request({
      api: "conference.preJoin",
      method: "POST",
      body: {
        meetingCode
      }
    });
    return [data, null];
  } catch (error) {
    return [null, error.message.toString()];
  }
}
